import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as wasm from 'sched';
import Avatar from '../common/Avatar';
import { useDatabase } from '../../hooks/useDatabase';
import { AllocationModal, type AllocationBarInfo } from './AllocationModal';
import {
  CalendarTimeline,
  type CalendarBar,
  type CalendarRow,
  type VariantStyle,
  type TimelineOverlayContext,
} from './CalendarTimeline';

function fmtEffort(tenths: number): string {
  if (tenths === 0) return '0';
  const days = tenths / 10;
  return days % 1 === 0 ? `${days}d` : `${days.toFixed(1)}d`;
}

type Variant = 'committed' | 'draft' | 'suggestion';

const VARIANT_STYLES: Record<Variant, VariantStyle> = {
  committed: {
    idle: 'bg-indigo-500/70 text-white',
    hover: 'bg-indigo-500/90 text-white',
  },
  draft: {
    idle: 'bg-amber-500/50 text-amber-100 border border-dashed border-amber-400/50',
    hover: 'bg-amber-500/70 text-amber-100 border border-dashed border-amber-400/50',
  },
  suggestion: {
    idle: 'bg-teal-500/15 text-teal-300/80 border border-dashed border-teal-500/30',
    hover: 'bg-teal-500/30 text-teal-300/80 border border-dashed border-teal-500/30',
  },
};

interface PlanningGridProps {
  result: wasm.PlanningGridResult;
  onAcceptSuggestion: (personId: string, dayIdx: number, suggestion: wasm.PlannedAllocationDto) => void;
  onAddAllocation: (personId: string, dayIdx: number, entityId: string, effort: number) => void;
  onRemoveDraft: (personId: string, dayIdx: number, draftIdx: number) => void;
}

interface ExtractedBars {
  committed: BarData[];
  draft: BarData[];
  suggestions: BarData[];
}

interface BarData {
  label: string;
  totalEffort: number;
  startIdx: number;
  endIdx: number;
  alloc: any;
  personId: string;
}

/** Extract contiguous bars per variant from a person's per-day cells. */
function extractBars(person: wasm.PersonPlanDto, days: any[], personId: string): ExtractedBars {
  const committed: BarData[] = [];
  const draft: BarData[] = [];
  const suggestions: BarData[] = [];
  // Keyed lookup for run-length merging of adjacent days into bars.
  const seen: Record<string, BarData> = {};

  for (let i = 0; i < days.length; i++) {
    const cell = person.cells[i.toString()];
    if (!cell) continue;
    for (const [field, target] of [
      ['committed', committed],
      ['draft', draft],
      ['suggestions', suggestions],
    ] as const) {
      const allocs: any[] = (cell as any)[field] ?? [];
      for (const alloc of allocs) {
        const key = `${field}:${alloc.allocation_id || `${alloc.label}-${(alloc.entity as any)?.id ?? ''}`}`;
        const existing = seen[key];
        if (existing) {
          existing.endIdx = i;
        } else {
          const bar: BarData = {
            label: alloc.label,
            totalEffort: (alloc as any).effort ?? 0,
            startIdx: i,
            endIdx: i,
            alloc,
            personId,
          };
          seen[key] = bar;
          target.push(bar);
        }
      }
    }
  }
  return { committed, draft, suggestions };
}

export const PlanningGrid: React.FC<PlanningGridProps> = ({ result }) => {
  const days: any[] = (result as any).days ?? [];
  const periods: any[] = (result as any).periods ?? [];
  const people = result.people;
  const { workspaceId, database, applyCommand } = useDatabase();

  const [revealedPeriodsByPerson, setRevealedPeriodsByPerson] = useState<Record<string, Set<number>>>({});

  const [modalState, setModalState] = useState<{
    bar: AllocationBarInfo;
    variant: Variant;
    personCells: Record<string, any>;
  } | null>(null);

  const sortedPeople = useMemo(
    () => Object.entries(people).sort(([, a], [, b]) => a.name.localeCompare(b.name)),
    [people],
  );

  // Build one CalendarRow per person. Suggestions are only rendered for
  // periods the user has "revealed" via the per-person toggle.
  const rows: CalendarRow[] = useMemo(() => {
    return sortedPeople.map(([personId, person]) => {
      const { committed, draft, suggestions } = extractBars(person, days, personId);
      const revealed = revealedPeriodsByPerson[personId] ?? new Set<number>();

      const openBar = (bar: BarData, variant: Variant) => {
        setModalState({
          bar: bar as AllocationBarInfo,
          variant,
          personCells: person.cells as Record<string, any>,
        });
      };

      const toCalendarBar = (bar: BarData, variant: Variant): CalendarBar => ({
        key: `${variant}:${bar.startIdx}:${bar.alloc.allocation_id ?? bar.label}`,
        label: bar.label,
        startIdx: bar.startIdx,
        endIdx: bar.endIdx,
        variant,
        meta: fmtEffort(bar.totalEffort),
        tooltipSuffix:
          variant === 'committed'
            ? '(click to edit)'
            : variant === 'suggestion'
              ? '(click to schedule)'
              : undefined,
        onClick: () => openBar(bar, variant),
      });

      const bars: CalendarBar[] = [
        ...committed.map((b) => toCalendarBar(b, 'committed')),
        ...draft.map((b) => toCalendarBar(b, 'draft')),
      ];
      // Suggestions: only show in revealed periods.
      // We compute period-index per suggestion from the day's period_idx.
      for (const s of suggestions) {
        const periodIdx = days[s.startIdx]?.period_idx;
        if (periodIdx === undefined || !revealed.has(periodIdx)) continue;
        bars.push(toCalendarBar(s, 'suggestion'));
      }

      // Per-row overlay: "suggestions" toggle buttons per period that has
      // suggestions OR remaining capacity.
      const timelineOverlay = (ctx: TimelineOverlayContext) => {
        const periodsWithContent = new Set<number>();
        for (const s of suggestions) {
          const pi = days[s.startIdx]?.period_idx;
          if (pi !== undefined) periodsWithContent.add(pi);
        }
        for (let pi = 0; pi < ctx.periodBoundaries.length; pi++) {
          const pb = ctx.periodBoundaries[pi];
          let periodCapacity = 0;
          let periodUtilized = 0;
          for (let di = pb.startIdx; di <= pb.endIdx && di < ctx.days.length; di++) {
            const cell = person.cells[di.toString()];
            if (cell) {
              periodCapacity += (cell as any).capacity ?? 0;
              periodUtilized += (cell as any).utilized ?? 0;
            }
          }
          if (periodCapacity > periodUtilized) periodsWithContent.add(pi);
        }

        return (
          <>
            {ctx.periodBoundaries.map((pb, i) => {
              if (!periodsWithContent.has(i)) return null;
              const isRevealed = revealed.has(i);
              return (
                <button
                  key={`sug-${i}`}
                  className={`absolute bottom-0.5 z-[6] text-[8px] whitespace-nowrap -translate-x-1/2 transition-opacity ${
                    isRevealed
                      ? 'opacity-30 hover:opacity-60 text-cm-text-muted'
                      : 'opacity-0 group-hover/timeline:opacity-20 hover:!opacity-50 text-cm-text-muted'
                  }`}
                  style={{ left: `${pb.leftPct + pb.widthPct / 2}%` }}
                  onClick={() =>
                    setRevealedPeriodsByPerson((prev) => {
                      const next = { ...prev };
                      const set = new Set(next[personId] ?? new Set<number>());
                      if (set.has(i)) set.delete(i);
                      else set.add(i);
                      next[personId] = set;
                      return next;
                    })
                  }
                >
                  {isRevealed ? 'hide' : 'suggestions'}
                </button>
              );
            })}
          </>
        );
      };

      return {
        key: personId,
        labelContent: (
          <>
            <Avatar imageUrl={person.avatar_url} name={person.name} showTooltip={false} size="sm" />
            <Link
              to={`/workspaces/${workspaceId}/people/${personId}`}
              className="text-xs font-medium truncate hover:text-cm-accent-blue transition-colors"
            >
              {person.name}
            </Link>
          </>
        ),
        bars,
        timelineOverlay,
      };
    });
  }, [sortedPeople, days, revealedPeriodsByPerson, workspaceId]);

  return (
    <>
      <CalendarTimeline
        days={days}
        periods={periods}
        rows={rows}
        variantStyles={VARIANT_STYLES}
        emptyMessage="No schedule data."
      />

      {modalState && (
        <AllocationModal
          bar={modalState.bar}
          variant={modalState.variant}
          days={days}
          workspaceId={workspaceId}
          database={database}
          personCells={modalState.personCells}
          applyCommand={applyCommand}
          onClose={() => setModalState(null)}
        />
      )}
    </>
  );
};
